import { bound } from "@frui.ts/helpers";
import type { interfaces } from "inversify";
import { computed, observable } from "mobx";
import UnpairedPayment from "entities/unpairedPayment";
import type ChargeOrderSearch from "entities/chargeOrderSearchResponse";
import PaymentsPairingRepository from "repositories/paymentsPairingRepository";
import ConfirmationService from "services/confirmationService";
import EventBus, { paymentPaired } from "services/eventBus";
import LocalizationService from "services/localizationService";
import NotificationService, { NotificationType } from "services/notificationService";
import SecurityService from "services/securityService";
import OptionsService from "services/optionsService";
import { allowToPair } from "../rights";
import UnpairedPaymentListViewModel from "../unpairedPaymentListViewModel";

export default class PairingByOrderPaymentsListViewModel extends UnpairedPaymentListViewModel {
  @observable openPayment?: UnpairedPayment;

  constructor(
    private order: ChargeOrderSearch,
    protected localizationService: LocalizationService,
    protected repository: PaymentsPairingRepository,
    protected eventBus: EventBus,
    protected securityService: SecurityService,
    protected notificationService: NotificationService,
    protected confirmationService: ConfirmationService,
    protected optionsService: OptionsService
  ) {
    super();
    this.navigationName = "";
  }

  @computed
  get allowToPair() {
    return allowToPair(this.securityService);
  }

  @bound
  async onPairPaymentClick(payment: UnpairedPayment) {
    if (!this.allowToPair) {
      this.notificationService.addNotAllowedError();
      return;
    }

    this.openPayment = payment;
    const confirmed = await this.confirmationService.showConfirmation(this.translate("confirmOrderMatch.title"), {
      confirmButton: { text: this.translate("confirmOrderMatch.confirm"), variant: "danger" },
    });

    if (confirmed) {
      try {
        await this.repository.pairPaymentWithOrder(payment, this.order);
        this.notificationService.add({ title: this.translate("paired"), type: NotificationType.SUCCESS });
        this.eventBus.publish(paymentPaired({ payment, order: this.order }));
      } catch (e) {
        // General error handle is good enough
      }
    }
    this.openPayment = undefined;
  }

  static Factory({ container }: interfaces.Context) {
    return (item: ChargeOrderSearch) =>
      new PairingByOrderPaymentsListViewModel(
        item,
        container.get(LocalizationService),
        container.get(PaymentsPairingRepository),
        container.get(EventBus),
        container.get(SecurityService),
        container.get(NotificationService),
        container.get(ConfirmationService),
        container.get(OptionsService)
      );
  }
}
